import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Shield, CheckCircle2, X } from "lucide-react";
import emailjs from "@emailjs/browser";


export default function EnquiryForm({ onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    caseType: "",
  });


  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const formRef = useRef(null);


  const advisoryOptions = [
    "Corporate Advisory",
    "Litigation",
    "Regulatory Compliance",
    "Tax & Finance",
    "Intellectual Property",
    "Employment Law",
    "Real Estate Law",
    "Other",
  ];


  // Initialize EmailJS with your public key
  useEffect(() => {
    emailjs.init("Z3O0dYQfZrm5XgwPt");
  }, []);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };


    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }


    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };


  const handleSelectAdvisory = (option) => {
    setFormData({ ...formData, caseType: option });
    setDropdownOpen(false);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("idle");


    try {
      await emailjs.send(
        "service_lg9kn3k",
        "template_err2ccp",
        formData,
        "Z3O0dYQfZrm5XgwPt"
      );


      setStatus("success");
      setFormData({
        name: "",
        mobile: "",
        email: "",
        caseType: "",
      });


      if (onClose) {
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };


  return (
    <motion.div
      ref={formRef}
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="
        w-full mx-auto
        max-w-md sm:max-w-lg
        max-h-[90vh] sm:max-h-[85vh]
        bg-white
        border-4 border-black
        rounded-2xl
        p-4 sm:p-6
        relative
        shadow-2xl
        overflow-visible
        scrollbar-thin scrollbar-thumb-black scrollbar-track-gray-100
      "
    >
      {/* Dropdown positioned at top of form */}
      <AnimatePresence>
        {dropdownOpen && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="
              absolute top-0 left-0 right-0 z-[9999]
              bg-white border-4 border-black rounded-2xl
              shadow-2xl max-h-[400px] overflow-y-auto
              scrollbar-thin scrollbar-thumb-black scrollbar-track-gray-100
            "
          >
            {/* Dropdown Header */}
            <div className="p-3 bg-black rounded-t-2xl border-b-4 border-black sticky top-0 z-10">
              <h4 className="text-white text-base font-bold uppercase tracking-wide text-center">
                Advisory Options
              </h4>
            </div>


            {/* Options List */}
            <div className="p-2">
              {advisoryOptions.map((option, index) => (
                <motion.button
                  key={index}
                  type="button"
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05, duration: 0.2 }}
                  onClick={() => handleSelectAdvisory(option)}
                  className="
                    w-full text-left px-4 py-3 text-base font-semibold
                    hover:bg-gray-50 hover:text-black hover:shadow-md
                    border-b border-gray-200 last:border-b-0
                    transition-all duration-200 flex items-center justify-between
                    bg-white text-gray-800
                    hover:border hover:border-black/20
                  "
                >
                  <span className="truncate">{option}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Close Button */}
      {onClose && (
        <motion.button
          onClick={onClose}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="
            absolute top-3 right-3 z-20
            w-9 h-9 rounded-full
            bg-black text-white
            flex items-center justify-center
            hover:bg-gray-800
            transition-all duration-200
            border-2 border-black
          "
        >
          <X className="w-5 h-5" />
        </motion.button>
      )}


      {/* Header */}
      <div className="text-center mb-4 sm:mb-6 pt-2">
        <h3 className="text-xl sm:text-2xl font-bold text-black mb-1 sm:mb-2">
          Get Consultation
        </h3>
        <p className="text-xs sm:text-sm text-gray-600 max-w-[90%] mx-auto leading-tight">
          Share your details and our experts will contact you shortly
        </p>
      </div>


      {/* Status Messages */}
      <AnimatePresence>
        {status === "success" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="mb-4 p-3 border-2 border-green-500 bg-green-50 rounded-lg text-center"
          >
            <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto mb-2" />
            <p className="text-base font-bold text-green-800">Enquiry Sent!</p>
            <p className="text-xs text-green-700">We'll contact you soon</p>
          </motion.div>
        )}


        {status === "error" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="mb-4 p-3 border-2 border-red-500 bg-red-50 rounded-lg text-center"
          >
            <p className="text-base font-bold text-red-800 mb-1">Failed to send</p>
            <p className="text-xs text-red-700">Please try again.</p>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        {/* Name */}
        <div>
          <label className="block text-xs sm:text-sm font-bold text-black mb-1.5">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your full name"
            className="
              w-full rounded-xl border-2 border-black
              px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg
              text-black
              focus:border-gray-800 focus:outline-none
              transition-all duration-200 bg-gray-50
              hover:bg-white
            "
          />
        </div>


        {/* Mobile */}
        <div>
          <label className="block text-xs sm:text-sm font-bold text-black mb-1.5">
            Mobile Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            name="mobile"
            required
            value={formData.mobile}
            onChange={handleChange}
            placeholder="10-digit mobile number"
            className="
              w-full rounded-xl border-2 border-black
              px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg
              text-black
              focus:border-gray-800 focus:outline-none
              transition-all duration-200 bg-gray-50
              hover:bg-white
            "
          />
        </div>


        {/* Email */}
        <div>
          <label className="block text-xs sm:text-sm font-bold text-black mb-1.5">
            Email <span className="text-gray-500">(Optional)</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            className="
              w-full rounded-xl border-2 border-black
              px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg
              text-black
              focus:border-gray-800 focus:outline-none
              transition-all duration-200 bg-gray-50
              hover:bg-white
            "
          />
        </div>


        {/* Advisory Type Dropdown Trigger */}
        <div className="relative z-10">
          <label className="block text-xs sm:text-sm font-bold text-black mb-1.5">
            Advisory Type <span className="text-red-500">*</span>
          </label>

          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="
              w-full rounded-xl border-2 border-black px-3 sm:px-4 py-2.5 sm:py-3 text-left
              bg-gray-50 text-base sm:text-lg
              focus:border-gray-800 focus:outline-none
              hover:bg-white transition-all duration-200
              flex items-center justify-between
            "
          >
            <span className={`${formData.caseType ? "font-bold text-black" : "text-gray-500"} text-base sm:text-lg`}>
              {formData.caseType || "Select Advisory Type"}
            </span>
            <ChevronDown className={`w-4 sm:w-5 h-4 sm:h-5 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
          </button>
        </div>


        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={loading || !formData.caseType}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="
            w-full py-3 sm:py-4 rounded-xl border-2 border-black bg-black
            text-white font-bold text-base sm:text-lg
            hover:bg-gray-900 hover:border-gray-900
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all duration-200 uppercase tracking-wide
          "
        >
          {loading ? "Sending..." : "Submit Enquiry"}
        </motion.button>


        {/* Security Badge */}
        <div className="flex items-center justify-center pt-2 pb-4">
          <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-black mr-1" />
          <p className="text-xs font-semibold text-gray-700 leading-tight">
            Secure & Confidential
          </p>
        </div>
      </form>
    </motion.div>
  );
}
